/**
 * NodeFrictionMap — Carte des frictions contextuelle d'un nœud de la rivière.
 *
 * V23.15.61 — Affichée dans le panneau détail à droite quand un nœud est
 * sélectionné. Liste 3-5 frictions systémiques attendues au niveau causal
 * du nœud (régulateur, fournisseur critique, partenaire amont, pair,
 * partenaire aval, client, observateur, self), contextualisées par les
 * autres acteurs de la rivière.
 *
 * Backend : POST /api/workspace/origination/value-chain/friction-map
 * Cache 24h par (org, node_id, signature rivière).
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AlertTriangle, ShieldCheck, Loader2, RefreshCw, ChevronDown } from "lucide-react";

const SEVERITY_META = {
  faible: { color: "#1D9E75", label: "Faible", bar: 33 },
  moyen: { color: "#EF9F27", label: "Moyen", bar: 66 },
  eleve: { color: "#E24B4A", label: "Élevé", bar: 100 },
};

const LEVEL_ICON_COLOR = {
  regulateur: "#7F77DD",
  fournisseur_critique: "#7F77DD",
  partenaire_amont: "#7F77DD",
  pair_immediat: "#EF9F27",
  partenaire_aval: "#1D9E75",
  client_final: "#1D9E75",
  observateur: "#71717A",
  self: "#EF9F27",
};

export default function NodeFrictionMap({ nodeId, testIdPrefix = "node-friction" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(true);

  const fetchMap = async (force = false) => {
    if (!nodeId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.post(
        `/workspace/origination/value-chain/friction-map?force=${force ? "true" : "false"}`,
        { node_id: nodeId },
      );
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Analyse impossible.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setData(null);
    setError(null);
    if (nodeId) fetchMap(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeId]);

  if (!nodeId) return null;

  const levelColor = data ? LEVEL_ICON_COLOR[data.causal_level] || "#71717A" : "#71717A";

  return (
    <div className="pt-3 border-t border-white/5" data-testid={testIdPrefix}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between text-left group"
        data-testid={`${testIdPrefix}-toggle`}
      >
        <div className="inline-flex items-center gap-1.5">
          <AlertTriangle className="w-3 h-3" style={{ color: levelColor }} />
          <span
            className="text-[10px] uppercase tracking-wider font-semibold"
            style={{ color: levelColor }}
          >
            Frictions du niveau
          </span>
          {data?.overall_score != null && (
            <span
              className="text-[9px] px-1.5 py-0.5 rounded border tabular-nums"
              style={{ color: levelColor, borderColor: `${levelColor}66`, background: `${levelColor}12` }}
              title="Charge de friction globale à ce niveau"
              data-testid={`${testIdPrefix}-score`}
            >
              {data.overall_score}/100
            </span>
          )}
        </div>
        <ChevronDown
          className={`w-3 h-3 text-zinc-500 transition-transform ${expanded ? "" : "-rotate-90"}`}
        />
      </button>

      {expanded && (
        <div className="mt-2 space-y-2">
          {loading && (
            <div className="text-[11px] text-zinc-500 inline-flex items-center gap-1.5 py-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Analyse du niveau causal…
            </div>
          )}
          {error && (
            <div className="text-[11px] text-[#E24B4A] py-1" data-testid={`${testIdPrefix}-error`}>{error}</div>
          )}
          {!loading && !error && data && (
            <>
              {data.level_label && (
                <div className="text-[10px] text-zinc-500 italic" data-testid={`${testIdPrefix}-level-label`}>
                  {data.level_label}
                </div>
              )}
              {Array.isArray(data.frictions) && data.frictions.length > 0 ? (
                <ul className="space-y-2" data-testid={`${testIdPrefix}-list`}>
                  {data.frictions.map((f, i) => {
                    const sev = SEVERITY_META[f.severity] || SEVERITY_META.moyen;
                    return (
                      <li
                        key={i}
                        className="rounded border border-white/5 bg-white/[0.02] p-2 space-y-1"
                        data-testid={`${testIdPrefix}-item-${i}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="text-[11.5px] font-semibold text-zinc-100 leading-snug flex-1">
                            {f.label}
                          </div>
                          <span
                            className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded flex-shrink-0"
                            style={{ background: `${sev.color}18`, color: sev.color, border: `1px solid ${sev.color}55` }}
                          >
                            {sev.label}
                          </span>
                        </div>
                        <div className="h-1 bg-white/[0.06] rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${sev.bar}%`, background: sev.color }} />
                        </div>
                        <p className="text-[10.5px] text-zinc-400 leading-snug">{f.description}</p>
                        {f.mitigation && (
                          <p className="text-[10.5px] leading-snug inline-flex items-start gap-1 pt-0.5">
                            <ShieldCheck className="w-2.5 h-2.5 text-[#1D9E75] flex-shrink-0 mt-0.5" />
                            <span className="text-zinc-300"><span className="text-zinc-500">Contenir :</span> {f.mitigation}</span>
                          </p>
                        )}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="text-[11px] text-zinc-500 italic py-1">
                  Aucune friction structurelle notable à ce niveau.
                </div>
              )}
              {data.strategic_read && (
                <p className="text-[11px] text-zinc-300 leading-snug pt-1 border-t border-white/5" data-testid={`${testIdPrefix}-read`}>
                  <span className="text-zinc-500">Lecture : </span>{data.strategic_read}
                </p>
              )}
              <button
                type="button"
                onClick={() => fetchMap(true)}
                className="text-[10px] text-zinc-500 hover:text-zinc-200 inline-flex items-center gap-1 pt-0.5"
                data-testid={`${testIdPrefix}-refresh`}
              >
                <RefreshCw className="w-2.5 h-2.5" /> Régénérer
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
